import * as React from 'react'

import { Box, LineChart } from '@factorialco/deprecated-design-system'
import { Point } from '@nivo/line'
import { isSameDay } from 'date-fns'
import { InsightsHeadcountFragment } from 'generated/resources/graphql'
import { isNil } from 'lodash'
import { useWindowSize } from 'usehooks-ts'

import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'

import { HeadcountWidgetAction } from 'modules/insights/lib/events'
import magnitude from 'modules/insights/lib/magnitude'

import { type Cycle } from '../../../../core/components/DateNavigator/cycleUtils'
import DraggableNode from '../../../../core/components/DraggableNode'
import { formatBottomAxisDate } from '../../../lib/dateFormatUtils'
import { ClickableChartBottomTick } from './ClickableChartBottomTick'
import { HeadcountTooltip } from './HeadcountTooltip'
import {
  HeadcountEvolutionData,
  getColorHeadCount,
  getColorJoiners,
  getColorLeavers,
} from './headcountEvolutionUtils'

const scaleAxisY = (data: HeadcountEvolutionData) => {
  const maxY = data
    .flatMap((series) => series.data)
    .map((point) => point.y)
    .reduce((a, b) => Math.max(a, b))

  return magnitude(maxY)
}

type Props = {
  cycle: Cycle
  headcount: HeadcountEvolutionData
  headcountEvolution: InsightsHeadcountFragment[]
  selectedXAxis?: Date
  onXAxisSelected?: (xAxis: Date, action: HeadcountWidgetAction) => void
}

const ChartHeadcountEvolution = ({
  cycle,
  headcount,
  headcountEvolution,
  selectedXAxis,
  onXAxisSelected,
}: Props) => {
  const { width: windowWidth } = useWindowSize()
  const data = headcount
  const scaleY = scaleAxisY(data)

  const handleFormatBottomAxisText = useCallback(
    (value) => formatBottomAxisDate(cycle, value, windowWidth),
    [cycle, windowWidth]
  )

  const chartColors = [getColorHeadCount(), getColorJoiners(), getColorLeavers()]
  const margin = { top: 50, right: 40, bottom: 40, left: 40 }

  /*
    Logic to handle the "sticky" tooltip.
    - If the mouse is on the chart, we show the tooltip in those coords
    - If the mouse is on the tooltip, we show the tooltip in the last known coords
    - The point of data of the chart is also remembered
  */
  const coordinates = useMouseCoords()
  const [currentCoordinates, setCurrentCoordinates] = React.useState(coordinates)
  const [point, setPoint] = React.useState<Point>()
  const [currentPoint, setCurrentPoint] = React.useState<Point>()
  const [mouseHoveringTooltip, setMouseHoveringTooltip] = React.useState(false)

  const handleMouseEnterTooltip = useUnsafeCallback(() => {
    setMouseHoveringTooltip(true)
  }, [setMouseHoveringTooltip])

  const handleMouseLeaveTooltip = useUnsafeCallback(() => {
    setMouseHoveringTooltip(false)
  }, [setMouseHoveringTooltip])

  const handleChartMousePointChanged = useUnsafeCallback(
    (nextPoint: Point | undefined) => {
      setPoint(nextPoint)
    },
    [setPoint]
  )

  const handleChartMouseLeave = useUnsafeCallback(() => {
    setPoint(undefined)
  }, [setPoint])

  useUnsafeEffect(() => {
    if (!mouseHoveringTooltip) {
      setCurrentCoordinates(coordinates)
      setCurrentPoint(point)
    }
  }, [mouseHoveringTooltip, coordinates, point, setCurrentCoordinates, setCurrentPoint])

  const selectXAxis = useUnsafeCallback(
    (date: Date, action: HeadcountWidgetAction) => {
      onXAxisSelected?.(date, action)
    },
    [onXAxisSelected]
  )

  const handleOpenTooltipDetails = useUnsafeCallback(() => {
    setMouseHoveringTooltip(false)
    selectXAxis(currentPoint?.data.x as Date, 'tooltip')
    setPoint(undefined)
  }, [currentPoint, selectXAxis])

  const handleBottomTickClick = useUnsafeCallback(
    (date: Date) => {
      selectXAxis(date, 'bottom-axis')
    },
    [selectXAxis]
  )

  // Clear tooltip lock if we aren't rendering it anymore
  useUnsafeEffect(() => {
    if (!currentPoint) {
      setMouseHoveringTooltip(false)
    }
  }, [currentPoint])

  const handleRenderBottomAxisTick = useUnsafeCallback<
    NonNullable<React.ComponentProps<typeof LineChart>['renderBottomAxisTick']>
  >(
    (props) => {
      return (
        <ClickableChartBottomTick
          {...props}
          selected={!isNil(selectedXAxis) && isSameDay(selectedXAxis, props.value)}
          onTickClick={handleBottomTickClick}
        />
      )
    },
    [selectedXAxis, handleBottomTickClick]
  )

  return (
    <>
      <Box minHeight="s300" height="s300">
        <LineChart
          data={data}
          formatBottom={handleFormatBottomAxisText}
          margin={margin}
          scaleY={scaleY}
          colors={chartColors}
          maxBottomAxisTicks={12}
          renderBottomAxisTick={handleRenderBottomAxisTick}
          onMouseEnter={handleChartMousePointChanged}
          onMouseMove={handleChartMousePointChanged}
          onMouseLeave={handleChartMouseLeave}
        />
      </Box>

      {currentPoint && (
        <DraggableNode
          coordinates={currentCoordinates}
          attachment="bottom center"
          onMouseEnter={handleMouseEnterTooltip}
          onMouseLeave={handleMouseLeaveTooltip}
        >
          <HeadcountTooltip
            headcountEvolution={headcountEvolution}
            data={data}
            point={currentPoint}
            onOpenDetails={handleOpenTooltipDetails}
          />
        </DraggableNode>
      )}
    </>
  )
}

export default ChartHeadcountEvolution
